import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardInfo, KeyboardStyle } from '@capacitor/keyboard';
import { Haptics, ImpactStyle } from '@capacitor/haptics';
import { App, AppState } from '@capacitor/app';

/**
 * Centralized service for Capacitor native features
 * Provides type-safe, consistent access to native capabilities
 * 
 * This service abstracts all Capacitor plugin interactions,
 * making it easier to maintain, test, and extend native functionality.
 */
class CapacitorService {
  /**
   * Check if running in native app
   */
  isNativeApp(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Get current platform
   */
  getPlatform(): 'ios' | 'android' | 'web' {
    return Capacitor.getPlatform() as 'ios' | 'android' | 'web';
  }

  /**
   * Check if running on iOS
   */
  isIOS(): boolean {
    return this.getPlatform() === 'ios';
  }

  /**
   * Check if running on Android
   */
  isAndroid(): boolean {
    return this.getPlatform() === 'android';
  }

  /**
   * Check if running on web
   */
  isWeb(): boolean {
    return this.getPlatform() === 'web';
  }

  /**
   * Trigger haptic feedback
   * @param style - The intensity of the haptic feedback
   */
  async triggerHaptic(style: 'light' | 'medium' | 'heavy' = 'light'): Promise<void> {
    if (!this.isNativeApp()) return;

    try {
      const impactStyle = 
        style === 'light' ? ImpactStyle.Light 
        : style === 'medium' ? ImpactStyle.Medium 
        : ImpactStyle.Heavy;
      await Haptics.impact({ style: impactStyle });
    } catch (error) {
      // Silently fail - haptics not available or not supported
      console.debug('Haptics not available:', error);
    }
  }

  /**
   * Hide keyboard
   */
  async hideKeyboard(): Promise<void> {
    if (!this.isNativeApp()) return;

    try {
      await Keyboard.hide();
    } catch (error) {
      console.debug('Keyboard hide not available:', error);
    }
  }

  /**
   * Show keyboard
   */
  async showKeyboard(): Promise<void> {
    if (!this.isNativeApp()) return;

    try {
      await Keyboard.show();
    } catch (error) {
      console.debug('Keyboard show not available:', error);
    }
  }

  /**
   * Set keyboard style (iOS only)
   * @param isDark - Whether to use dark keyboard style
   */
  async setKeyboardStyle(isDark: boolean): Promise<void> {
    if (!this.isNativeApp() || !this.isIOS()) return;

    try {
      await Keyboard.setStyle({
        style: isDark ? KeyboardStyle.Dark : KeyboardStyle.Light,
      });
    } catch (error) {
      console.debug('Keyboard setStyle not available:', error);
    }
  }

  /**
   * Add keyboard will show listener
   * @param callback - Callback function when keyboard will show
   * @returns Cleanup function to remove the listener
   */
  async addKeyboardWillShowListener(
    callback: (info: KeyboardInfo) => void
  ): Promise<() => void> {
    if (!this.isNativeApp()) {
      return () => {}; // Return no-op cleanup
    }

    try {
      const listener = await Keyboard.addListener('keyboardWillShow', callback as any);
      return () => listener.remove();
    } catch (error) {
      console.debug('Keyboard listener not available:', error);
      return () => {};
    }
  }

  /**
   * Add keyboard will hide listener
   * @param callback - Callback function when keyboard will hide
   * @returns Cleanup function to remove the listener
   */
  async addKeyboardWillHideListener(
    callback: (info: KeyboardInfo) => void
  ): Promise<() => void> {
    if (!this.isNativeApp()) {
      return () => {}; // Return no-op cleanup
    }

    try {
      const listener = await Keyboard.addListener('keyboardWillHide', callback as any);
      return () => listener.remove();
    } catch (error) {
      console.debug('Keyboard listener not available:', error);
      return () => {};
    }
  }

  /**
   * Add keyboard did show listener
   * @param callback - Callback function when keyboard did show
   * @returns Cleanup function to remove the listener
   */
  async addKeyboardDidShowListener(
    callback: (info: KeyboardInfo) => void
  ): Promise<() => void> {
    if (!this.isNativeApp()) {
      return () => {}; // Return no-op cleanup
    }

    try {
      const listener = await Keyboard.addListener('keyboardDidShow', callback as any);
      return () => listener.remove();
    } catch (error) {
      console.debug('Keyboard listener not available:', error);
      return () => {};
    }
  }

  /**
   * Add keyboard did hide listener
   * @param callback - Callback function when keyboard did hide
   * @returns Cleanup function to remove the listener
   */
  async addKeyboardDidHideListener(
    callback: (info: KeyboardInfo) => void
  ): Promise<() => void> {
    if (!this.isNativeApp()) {
      return () => {}; // Return no-op cleanup
    }

    try {
      const listener = await Keyboard.addListener('keyboardDidHide', callback as any);
      return () => listener.remove();
    } catch (error) {
      console.debug('Keyboard listener not available:', error);
      return () => {};
    }
  }

  /**
   * Remove all keyboard listeners
   */
  async removeAllKeyboardListeners(): Promise<void> {
    if (!this.isNativeApp()) return;

    try {
      await Keyboard.removeAllListeners();
    } catch (error) {
      console.debug('Keyboard removeAllListeners not available:', error);
    }
  }

  /**
   * Add Android hardware back button listener.
   * The callback receives `{ canGoBack: boolean }` — if canGoBack is false the app
   * would normally exit; callers should decide whether to call `App.exitApp()`.
   * @returns Cleanup function to remove the listener
   */
  async addBackButtonListener(
    callback: (data: { canGoBack: boolean }) => void
  ): Promise<() => void> {
    if (!this.isNativeApp()) return () => {};

    try {
      const listener = await App.addListener('backButton', callback as any);
      return () => listener.remove();
    } catch (error) {
      console.debug('Back button listener not available:', error);
      return () => {};
    }
  }

  /**
   * Exit the app (Android back-button-at-root behaviour)
   */
  async exitApp(): Promise<void> {
    if (!this.isNativeApp()) return;
    try {
      await App.exitApp();
    } catch (error) {
      console.debug('App.exitApp not available:', error);
    }
  }

  /**
   * Add app state change listener
   * @param callback - Callback function when app state changes
   * @returns Cleanup function to remove the listener
   */
  async addAppStateListener(
    callback: (state: AppState) => void
  ): Promise<() => void> {
    if (!this.isNativeApp()) {
      // For web, use visibility API as fallback
      const handleVisibilityChange = () => {
        callback({ isActive: !document.hidden });
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }

    try {
      const listener = await App.addListener('appStateChange', callback as any);
      return () => listener.remove();
    } catch (error) {
      console.debug('App state listener not available:', error);
      return () => {};
    }
  }

  /**
   * Remove all app state listeners
   */
  async removeAllAppStateListeners(): Promise<void> {
    if (!this.isNativeApp()) return;

    try {
      await App.removeAllListeners();
    } catch (error) {
      console.debug('App removeAllListeners not available:', error);
    }
  }

  /**
   * Get app info
   * @returns App information or null if not available
   */
  async getAppInfo(): Promise<{ name: string; id: string; version: string; build: string } | null> {
    if (!this.isNativeApp()) return null;

    try {
      return await App.getInfo();
    } catch (error) {
      console.debug('App info not available:', error);
      return null;
    }
  }

  /**
   * Get app state
   * @returns Current app state or null if not available
   */
  async getAppState(): Promise<AppState | null> {
    if (!this.isNativeApp()) {
      // For web, return based on document visibility
      return { isActive: !document.hidden };
    }

    try {
      return await App.getState();
    } catch (error) {
      console.debug('App state not available:', error);
      return null;
    }
  }
}

// Export singleton instance
export const capacitorService = new CapacitorService();
export default capacitorService;

